/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Experience Fit Layer
 * 
 * Post-engine assessment that evaluates how well each system option matches
 * customer priorities and lifestyle factors. This is a PRESENTATION-ONLY layer
 * that does NOT affect engine ranking, costs, or exclusions.
 * 
 * Produces dots (🟢/🔴/⚪) and detects conflicts (🟠) based on:
 * - Customer answers to experience questions
 * - System characteristics
 * - Practitioner-calibrated behavior rules
 */

import type {
  SystemRecInput,
  SystemRecOutput,
  RecommendationOption,
  ExperienceFitFactor,
  ExperienceImportance,
  ExperienceVerdict,
  SystemType,
} from './types';

// ─── Factor Identifiers ─────────────────────────────────────────────

export const EXPERIENCE_FACTORS = [
  'multiple_taps',
  'heating_during_high_hot_water_demand',
  'airing_clothes',
  'space',
  'reliability',
  'longevity',
  'efficiency',
  'being_green',
  'noise',
] as const;

export type ExperienceFactor = typeof EXPERIENCE_FACTORS[number];

// ─── Importance Mapping ─────────────────────────────────────────────

interface ImportanceMap {
  multiple_taps: ExperienceImportance;
  heating_during_high_hot_water_demand: ExperienceImportance;
  airing_clothes: ExperienceImportance;
  space: ExperienceImportance;
  reliability: ExperienceImportance;
  longevity: ExperienceImportance;
  efficiency: ExperienceImportance;
  being_green: ExperienceImportance;
  noise: ExperienceImportance;
}

/**
 * Derive factor importance from experience question answers
 */
function deriveImportance(input: SystemRecInput): ImportanceMap {
  const eq = input.experienceQuestions || {};
  
  // Q1: Simultaneous hot water -> multiple_taps importance
  const multipleTabsImportance: ExperienceImportance = 
    eq.simultaneousHotWater === 'most_days' ? 'very_important' :
    eq.simultaneousHotWater === 'occasionally' ? 'important' :
    'not_important';
  
  // Q2: Heating during hot water -> heating_during_high_hot_water_demand importance
  const heatingDuringHotWaterImportance: ExperienceImportance =
    eq.heatingDuringHotWater || 'not_important';
  
  // Q3: Airing cupboard -> airing_clothes importance
  const airingClothesImportance: ExperienceImportance =
    eq.airingCupboard === 'yes_regularly' ? 'very_important' :
    eq.airingCupboard === 'occasionally' ? 'important' :
    'not_important';
  
  // Q4: Space pressure -> space importance
  const spaceImportance: ExperienceImportance =
    eq.spacePressure === 'very_tight' ? 'very_important' :
    eq.spacePressure === 'some_flexibility' ? 'important' :
    'not_important';
  
  // Q5: Operating style -> reliability/longevity/efficiency importance
  const operatingStyle = eq.operatingStyle || 'balanced';
  let reliabilityImportance: ExperienceImportance = 'important';
  let longevityImportance: ExperienceImportance = 'important';
  let efficiencyImportance: ExperienceImportance = 'important';
  
  if (operatingStyle === 'quick_response') {
    // Don't penalize based on this preference
    reliabilityImportance = 'not_important';
    longevityImportance = 'not_important';
    efficiencyImportance = 'not_important';
  } else {
    // steady_consistent or balanced both value these
    reliabilityImportance = 'important';
    longevityImportance = 'important';
    efficiencyImportance = 'important';
  }
  
  // Q6: Noise sensitivity -> noise importance
  const noiseImportance: ExperienceImportance =
    eq.noiseSensitivity === 'very_sensitive' ? 'very_important' :
    eq.noiseSensitivity === 'somewhat' ? 'important' :
    'not_important';
  
  // Q8: Environmental intent -> being_green importance
  const beingGreenImportance: ExperienceImportance =
    eq.environmentalIntent === 'greenest_now' ? 'very_important' :
    eq.environmentalIntent === 'ready_for_later' ? 'important' :
    'not_important';
  
  return {
    multiple_taps: multipleTabsImportance,
    heating_during_high_hot_water_demand: heatingDuringHotWaterImportance,
    airing_clothes: airingClothesImportance,
    space: spaceImportance,
    reliability: reliabilityImportance,
    longevity: longevityImportance,
    efficiency: efficiencyImportance,
    being_green: beingGreenImportance,
    noise: noiseImportance,
  };
}

// ─── System Behavior Rules ──────────────────────────────────────────

interface SystemCharacteristics {
  isCombi: boolean;
  isStored: boolean;
  isUnvented: boolean;
  isOpenVented: boolean;
  isHeatPump: boolean;
  isSystemBoiler: boolean;
  isRegularBoiler: boolean;
  hasHighPowerHX: boolean; // e.g., Mixergy-style
  mainsPressure?: number;
  flowRate?: number;
}

/**
 * Extract system characteristics from option
 */
function getSystemCharacteristics(
  option: RecommendationOption,
  input: SystemRecInput
): SystemCharacteristics {
  const isCombi = option.boiler?.toLowerCase().includes('combi') || false;
  const isSystemBoiler = option.boiler?.toLowerCase().includes('system') || false;
  const isRegularBoiler = option.boiler?.toLowerCase().includes('regular') || false;
  const isHeatPump = option.systemType === 'heat_pump' || option.boiler?.toLowerCase().includes('heat pump') || false;
  
  const waterStrategy = input.hotWater?.strategy || option.water;
  const waterVariant = input.hotWater?.variant;
  const isUnvented = waterStrategy === 'unvented';
  const isOpenVented = waterStrategy === 'open_vented';
  const isStored = isUnvented || isOpenVented;
  const hasHighPowerHX = waterVariant === 'mixergy';
  
  return {
    isCombi,
    isStored,
    isUnvented,
    isOpenVented,
    isSystemBoiler,
    isRegularBoiler,
    isHeatPump,
    hasHighPowerHX,
    mainsPressure: input.mainsPressure,
    flowRate: input.flowRate,
  };
}

/**
 * Evaluate a single factor for an option
 */
function evaluateFactor(
  factor: ExperienceFactor,
  importance: ExperienceImportance,
  characteristics: SystemCharacteristics,
  input: SystemRecInput
): { verdict: ExperienceVerdict; comment?: string } {
  switch (factor) {
    case 'multiple_taps':
      return evaluateMultipleTaps(characteristics, input);
    
    case 'heating_during_high_hot_water_demand':
      return evaluateHeatingDuringHotWater(characteristics);
    
    case 'airing_clothes':
      return evaluateAiringClothes(characteristics);
    
    case 'space':
      return evaluateSpace(characteristics);
    
    case 'reliability':
      return evaluateReliability(characteristics);
    
    case 'longevity':
      return evaluateLongevity(characteristics);
    
    case 'efficiency':
      return evaluateEfficiency(characteristics);
    
    case 'being_green':
      return evaluateBeingGreen(characteristics, input);
    
    case 'noise':
      return evaluateNoise(characteristics);
    
    default:
      return { verdict: 'unknown' };
  }
}

// ─── Factor Evaluation Functions ────────────────────────────────────

function evaluateMultipleTaps(
  characteristics: SystemCharacteristics,
  input: SystemRecInput
): { verdict: ExperienceVerdict; comment?: string } {
  if (characteristics.isStored) {
    // Unvented (stored) supports strongly if mains flow >= 20 L/min
    if (characteristics.isUnvented) {
      if (characteristics.flowRate && characteristics.flowRate >= 20) {
        return {
          verdict: 'supports',
          comment: 'Stored water at mains pressure handles multiple outlets well',
        };
      }
      return {
        verdict: 'supports',
        comment: 'Stored water typically handles multiple outlets',
      };
    }
    
    // Open vented generally supports but is installation-dependent
    if (characteristics.isOpenVented) {
      return {
        verdict: 'supports',
        comment: 'Stored water can supply multiple taps (pipe sizing dependent)',
      };
    }
    
    return { verdict: 'supports' };
  }
  
  // Combi generally conflicts
  if (characteristics.isCombi) {
    return {
      verdict: 'conflicts',
      comment: 'On-demand systems struggle with simultaneous use; cold tap can destabilize flow',
    };
  }
  
  return { verdict: 'unknown' };
}

function evaluateHeatingDuringHotWater(
  characteristics: SystemCharacteristics
): { verdict: ExperienceVerdict; comment?: string } {
  if (characteristics.isCombi) {
    return {
      verdict: 'conflicts',
      comment: 'Heating pauses during hot water use; hot water doesn\'t run out but heating stops',
    };
  }
  
  if (characteristics.isStored) {
    if (characteristics.hasHighPowerHX) {
      return {
        verdict: 'supports',
        comment: 'High-power heat exchanger recovers quickly while heating continues',
      };
    }
    return {
      verdict: 'supports',
      comment: 'Heating continues normally during hot water use',
    };
  }
  
  return { verdict: 'unknown' };
}

function evaluateAiringClothes(
  characteristics: SystemCharacteristics
): { verdict: ExperienceVerdict; comment?: string } {
  if (characteristics.isStored) {
    return {
      verdict: 'supports',
      comment: 'Cylinder keeps cupboard warm continuously for drying',
    };
  }
  
  if (characteristics.isCombi) {
    return {
      verdict: 'conflicts',
      comment: 'No stored water; cupboard no longer warmed; radiator workaround less effective',
    };
  }
  
  return { verdict: 'unknown' };
}

function evaluateSpace(
  characteristics: SystemCharacteristics
): { verdict: ExperienceVerdict; comment?: string } {
  if (characteristics.isCombi) {
    return {
      verdict: 'supports',
      comment: 'Compact system with no separate cylinder',
    };
  }
  
  if (characteristics.isStored) {
    return {
      verdict: 'conflicts',
      comment: 'Requires space for cylinder and associated pipework',
    };
  }
  
  return { verdict: 'unknown' };
}

function evaluateReliability(
  characteristics: SystemCharacteristics
): { verdict: ExperienceVerdict; comment?: string } {
  if (characteristics.isHeatPump) {
    return {
      verdict: 'supports',
      comment: 'Steady running when designed correctly',
    };
  }
  
  if (characteristics.isSystemBoiler) {
    return {
      verdict: 'supports',
      comment: 'Sealed system with stable modulation',
    };
  }
  
  if (characteristics.isRegularBoiler) {
    return {
      verdict: 'supports',
      comment: 'Open system; more forgiving; cheaper to maintain',
    };
  }
  
  if (characteristics.isCombi) {
    return {
      verdict: 'conflicts',
      comment: 'More cycling; exposed to mains water; shorter average life',
    };
  }
  
  return { verdict: 'unknown' };
}

function evaluateLongevity(
  characteristics: SystemCharacteristics
): { verdict: ExperienceVerdict; comment?: string } {
  // Similar to reliability
  if (characteristics.isHeatPump) {
    return {
      verdict: 'supports',
      comment: 'Long design life with proper maintenance',
    };
  }
  
  if (characteristics.isSystemBoiler) {
    return {
      verdict: 'supports',
      comment: 'Typically 15-20 year lifespan',
    };
  }
  
  if (characteristics.isRegularBoiler) {
    return {
      verdict: 'supports',
      comment: 'Robust design; 15-20 year lifespan',
    };
  }
  
  if (characteristics.isCombi) {
    return {
      verdict: 'conflicts',
      comment: 'Higher cycling and mains water exposure reduce lifespan',
    };
  }
  
  return { verdict: 'unknown' };
}

function evaluateEfficiency(
  characteristics: SystemCharacteristics
): { verdict: ExperienceVerdict; comment?: string } {
  if (characteristics.isHeatPump) {
    return {
      verdict: 'supports',
      comment: 'Highly efficient with steady operation',
    };
  }
  
  if (characteristics.isStored) {
    return {
      verdict: 'supports',
      comment: 'Modulating stored systems can be very efficient',
    };
  }
  
  if (characteristics.isCombi) {
    return {
      verdict: 'partial',
      comment: 'Efficient in theory but cycling can harm real-world performance',
    };
  }
  
  return { verdict: 'unknown' };
}

function evaluateBeingGreen(
  characteristics: SystemCharacteristics,
  input: SystemRecInput
): { verdict: ExperienceVerdict; comment?: string } {
  const solarIntent = input.solarIntent;
  const hasSolar = solarIntent === 'installed' || solarIntent === 'planned';
  
  if (characteristics.isHeatPump) {
    if (hasSolar) {
      return {
        verdict: 'supports',
        comment: 'Heat pump may be able to use solar PV for low-carbon heating',
      };
    }
    return {
      verdict: 'supports',
      comment: 'Lowest carbon footprint for heating',
    };
  }
  
  // Ready for later: stored system compatibility
  const eq = input.experienceQuestions || {};
  if (eq.environmentalIntent === 'ready_for_later' && characteristics.isStored) {
    return {
      verdict: 'supports',
      comment: 'Stored systems may be able to integrate future solar/smart controls',
    };
  }
  
  // Solar installed/planned: stored electric immersion / smart controls
  if (hasSolar && characteristics.isStored) {
    return {
      verdict: 'supports',
      comment: 'May be able to use solar for water heating via immersion',
    };
  }
  
  return { verdict: 'partial' };
}

function evaluateNoise(
  characteristics: SystemCharacteristics
): { verdict: ExperienceVerdict; comment?: string } {
  if (characteristics.isHeatPump) {
    return {
      verdict: 'conflicts',
      comment: 'Heat pumps have outdoor and indoor fans/pumps',
    };
  }
  
  if (characteristics.isCombi || characteristics.isSystemBoiler || characteristics.isRegularBoiler) {
    return {
      verdict: 'supports',
      comment: 'Gas boilers are relatively quiet',
    };
  }
  
  return { verdict: 'unknown' };
}

// ─── Conflict Detection ─────────────────────────────────────────────

interface ConflictCheck {
  isConflict: boolean;
  conflictNote?: string;
}

/**
 * Detect conflicts between customer priorities
 * Now system-aware: only flags conflicts for systems that create them
 */
function detectConflicts(
  factor: ExperienceFactor,
  importance: ImportanceMap,
  input: SystemRecInput,
  characteristics: SystemCharacteristics
): ConflictCheck {
  const eq = input.experienceQuestions || {};
  
  // Space conflicts with stored water requirements, but only if the system needs stored water
  if (factor === 'space' && importance.space !== 'not_important') {
    // Only conflict if this system requires stored water
    if (characteristics.isStored) {
      if (importance.multiple_taps !== 'not_important') {
        return {
          isConflict: true,
          conflictNote: 'Space pressure conflicts with stored water needed for multiple taps',
        };
      }
      if (importance.airing_clothes !== 'not_important') {
        return {
          isConflict: true,
          conflictNote: 'Space pressure conflicts with cylinder needed for airing cupboard warmth',
        };
      }
      if (importance.heating_during_high_hot_water_demand !== 'not_important') {
        return {
          isConflict: true,
          conflictNote: 'Space pressure conflicts with stored water needed for continuous heating',
        };
      }
    }
  }
  
  // being_green (greenest_now) conflicts with quick_response preference
  // This is system-agnostic - it's about customer expectation mismatch
  if (factor === 'being_green' && importance.being_green === 'very_important') {
    if (eq.operatingStyle === 'quick_response') {
      return {
        isConflict: true,
        conflictNote: 'Greenest options (heat pumps) work best with steady operation, not quick response',
      };
    }
  }
  
  // noise sensitivity conflicts with heat pumps - system-specific
  // Flag as conflict for both 'very_important' and 'important' noise sensitivity
  if (factor === 'noise' && characteristics.isHeatPump) {
    if (importance.noise === 'very_important') {
      return {
        isConflict: true,
        conflictNote: 'High noise sensitivity conflicts with heat pump requirements (outdoor unit fans and indoor circulation pumps)',
      };
    }
    if (importance.noise === 'important') {
      return {
        isConflict: true,
        conflictNote: 'Noise sensitivity may conflict with heat pump operation (consider unit placement)',
      };
    }
  }
  
  return { isConflict: false };
}

// ─── Dot Mapping ────────────────────────────────────────────────────

/**
 * Map importance × verdict to dots string
 */
function mapToDots(
  factor: ExperienceFactor,
  importance: ExperienceImportance,
  verdict: ExperienceVerdict
): string {
  // Factor-specific overrides (check BEFORE base mapping)
  if (factor === 'multiple_taps' && verdict === 'partial') {
    if (importance === 'important') return '🔴🔴';
    if (importance === 'very_important') return '🔴🔴🔴';
  }
  
  if (factor === 'heating_during_high_hot_water_demand' && verdict === 'partial') {
    if (importance === 'important' || importance === 'very_important') return '🔴🔴';
  }
  
  if (factor === 'airing_clothes' && verdict === 'unknown') {
    if (importance === 'very_important') return '🔴🔴';
  }
  
  // Base mapping
  if (importance === 'very_important') {
    if (verdict === 'supports') return '🟢🟢🟢🟢';
    if (verdict === 'partial') return '🔴🔴';
    if (verdict === 'conflicts') return '🔴🔴🔴🔴';
    if (verdict === 'unknown') return '🔴🔴';
  }
  
  if (importance === 'important') {
    if (verdict === 'supports') return '🟢🟢🟢';
    if (verdict === 'partial') return '🔴';
    if (verdict === 'conflicts') return '🔴🔴🔴';
    if (verdict === 'unknown') return '🔴';
  }
  
  if (importance === 'not_important') {
    if (verdict === 'supports') return '🟢';
    // partial/conflicts/unknown all map to neutral
    return '⚪';
  }
  
  // Fallback
  return '⚪';
}

// ─── Main Evaluation Function ───────────────────────────────────────

/**
 * Evaluate experience fit for all options
 * 
 * This function runs AFTER the engine produces recommendations.
 * It does NOT affect ranking, costs, or exclusions.
 */
export function evaluateExperienceFit(
  input: SystemRecInput,
  engineOutput: SystemRecOutput
): SystemRecOutput {
  // If no experience questions provided, return unchanged
  if (!input.experienceQuestions) {
    return engineOutput;
  }
  
  // Derive importance from answers
  const importance = deriveImportance(input);
  
  // Process all options (primary + alternatives + considered)
  const allOptions = [
    engineOutput.primary,
    ...engineOutput.alternatives,
    ...engineOutput.consideredOptions,
  ];
  
  for (const option of allOptions) {
    const characteristics = getSystemCharacteristics(option, input);
    const experienceFit: ExperienceFitFactor[] = [];
    
    for (const factor of EXPERIENCE_FACTORS) {
      const factorImportance = importance[factor];
      
      // Evaluate factor verdict
      const { verdict, comment: evalComment } = evaluateFactor(
        factor,
        factorImportance,
        characteristics,
        input
      );
      
      // Detect conflicts (now system-aware)
      const { isConflict, conflictNote } = detectConflicts(factor, importance, input, characteristics);
      
      // Map to dots
      const dots = mapToDots(factor, factorImportance, verdict);
      
      // Determine comment (only if dots contain red OR dots are 🟢🟢🟢🟢)
      let comment: string | undefined;
      if (dots.includes('🔴') || dots === '🟢🟢🟢🟢') {
        comment = evalComment;
      }
      
      experienceFit.push({
        factor,
        dots,
        verdict,
        isConflict,
        conflictNote,
        comment,
      });
    }
    
    option.experienceFit = experienceFit;
  }
  
  return engineOutput;
}
